import './CoralPdpStateStandards.scss'
import '../../../coral-library/css/_layout.scss'

import { Carousel, useIsWindowBiggerThan } from '@bob/library'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { CTA } from '@websrc/App/Components/CTA'
import { Markdown } from '@websrc/App/Components/Markdown/Markdown'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { SchoolFieldRef } from '@websrc/Checkout/Components/SchoolField/SchoolField'
import { STATES } from '@websrc/Checkout/States'
import { mapCoralWebPdpStateStandards } from '@websrc/DataMappers/mapSpot'
import React, { FC, useRef, useState } from 'react'
import Select, { components, SelectInstance } from 'react-select'

type CoralPdpStateStandardsProps = ReturnType<typeof mapCoralWebPdpStateStandards>

export const CoralPdpStateStandards: FC<CoralPdpStateStandardsProps> = ({
  header,
  content,
  image_group,
  state_standard_group,
  cards,
  note_label,
}) => {
  const schoolFieldRef = useRef<SchoolFieldRef | null>(null)
  const [result, setResult] = useState<(typeof state_standard_group)[0] | undefined>(undefined)
  const selectRef = useRef<SelectInstance<{ value: string; label: string }> | null>(null)

  //When selecting from dropdown
  const handleSelectChange = (selectedOption: { value: string; label: string } | null) => {
    if (!selectedOption) return

    const foundResult = state_standard_group.find(
      (group) => group.state_abbreviation === selectedOption.value
    )
    setResult(foundResult)
    if (schoolFieldRef.current) return foundResult
  }
  const isMobile = !useIsWindowBiggerThan(719)
  const card_variant =
    cards.length > 0 && cards.length < 5 ? (`v${cards.length}` as 'v1' | 'v2' | 'v3' | 'v4') : 'v4'

  // States that we should not show in the dropdown
  const stateBlackList = ['XX', 'AA', 'AE', 'AP', 'GU', 'PR', 'VI']

  const filteredStates = STATES.filter((state) => {
    // Filter out states that are in the blacklist
    return !stateBlackList.includes(state[0])
  })

  const selectOptions = filteredStates.map((state) => ({
    value: state[0],
    label: state[1],
  }))

  return (
    <div className={'container-coral-spot'}>
      <div className={'pdp-state-standards'}>
        <div className={'pdp-state-standards__top-wrapper'}>
          <h2 className={'pdp-state-standards__header'}>{header}</h2>
          <Markdown className={'pdp-state-standards__content'} markdown={content || ''} />
        </div>
        {state_standard_group.length > 0 && (
          <div className={'pdp-state-standards__bottom-wrapper'}>
            {image_group.image && (
              <Asset
                altText={image_group.alt_text}
                className={'pdp-state-standards__image'}
                lazyLoading={true}
                objectFit={'cover'}
                srcSetWidths={[200, 400]}
                thumbnail={image_group.image}
              />
            )}
            <div className={'pdp-state-standards__select-cta-wrapper'}>
              <Select
                aria-label={'Select state dropdown'}
                className={'base-select-container'}
                classNamePrefix={'base-select'}
                components={{ DropdownIndicator: CustomDropdownIndicator }}
                onChange={handleSelectChange}
                onMenuClose={() => {
                  if (selectRef.current) selectRef.current.blur()
                }}
                options={selectOptions}
                placeholder={'Select your state'}
                ref={selectRef}
                styles={{
                  singleValue: (provided, state) => ({
                    ...provided,
                    display: state.selectProps.menuIsOpen ? 'none' : 'block',
                  }),
                  placeholder: (provided, state) => ({
                    ...provided,
                    display: state.selectProps.menuIsOpen ? 'none' : 'block',
                  }),
                  dropdownIndicator: (provided, state) => ({
                    ...provided,
                    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease-in-out',
                  }),
                }}
              />
              <div className={'pdp-state-standards__button-wrapper'}>
                <CTA
                  className={`cta-override-button ${!result ? 'disabled' : ''}`}
                  cta={{
                    __ctaType: 'file',
                    file: result
                      ? { title: result.standard.title, url: result.standard.url }
                      : null,
                    text: 'View state standards',
                    newTab: true,
                  }}
                  onClick={() => {
                    matomo.trackEvent(
                      'engagement',
                      'Scince PDP',
                      ['coral download PDF button'],
                      [result?.state, result?.standard.title, result?.standard.url]
                    )
                  }}
                  type={'primary'}
                />
              </div>
            </div>
          </div>
        )}
        {cards.length > 0 && (
          <>
            <div className={`pdp-state-standards__cards-wrapper  m--${card_variant}`}>
              {isMobile ? (
                <Carousel itemsPerPage={1}>
                  {cards.map((card, index) => (
                    <div className={`pdp-state-standards__card`} key={index}>
                      <div className={'pdp-state-standards__card-label'}>{card.label}</div>
                      <div className={'pdp-state-standards__card-content-wrapper'}>
                        <div className={'pdp-state-standards__card-header'}>{card.header}</div>
                        <div className={'pdp-state-standards__card-content'}>{card.content}</div>
                      </div>
                    </div>
                  ))}
                </Carousel>
              ) : (
                cards.map((card, index) => (
                  <div
                    className={`pdp-state-standards__card-frame  m--${card_variant}`}
                    key={index}
                  >
                    <div className={'pdp-state-standards__card'}>
                      <div className={'pdp-state-standards__card-label'}>{card.label}</div>
                      <div className={'pdp-state-standards__card-content-wrapper'}>
                        <div className={'pdp-state-standards__card-header'}>{card.header}</div>
                        <div className={'pdp-state-standards__card-content'}>{card.content}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            {note_label && <Markdown markdown={note_label} />}
          </>
        )}
      </div>
    </div>
  )
}

//Custom dropdown for the react-select component
const CustomDropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <svg
        fill={'currentColor'}
        height={'27'}
        viewBox={'0 0 20 20'}
        width={'27'}
        xmlns={'http://www.w3.org/2000/svg'}
      >
        <g id={'chevron-group'}>
          <path
            d={
              'M9.48047 14.0195L3.48047 8.01953C3.16797 7.73828 3.16797 7.26953 3.48047 6.95703C3.76172 6.67578 4.23047 6.67578 4.54297 6.95703L10.0117 12.457L15.4805 6.98828C15.7617 6.67578 16.2305 6.67578 16.543 6.98828C16.8242 7.26953 16.8242 7.73828 16.543 8.01953L10.5117 14.0195C10.2305 14.332 9.76172 14.332 9.48047 14.0195Z'
            }
            id={'icon path'}
          />
        </g>
      </svg>
    </components.DropdownIndicator>
  )
}
